import { useState } from 'react';
import { Card, Table, Button, Spinner, Badge, Alert } from 'react-bootstrap';
import { Plus, Edit, Trash2 } from 'lucide-react';
import type {
  TierDefinition,
  CreateTierDefinitionRequest,
  UpdateTierDefinitionRequest,
} from '../../../../infrastructure/LoyaltyClient';
import { CreateTierModal } from './CreateTierModal';
import { UpdateTierDefinitionModal } from './UpdateTierDefinitionModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';

interface TierManagementEnhancedProps {
  tiers: TierDefinition[];
  isLoading: boolean;
  onCreateTier: (request: CreateTierDefinitionRequest) => Promise<void>;
  onUpdateTier: (args: { tierId: string; request: UpdateTierDefinitionRequest }) => Promise<void>;
  onDeleteTier: (tierId: string) => Promise<void>;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const TierManagementEnhanced = ({
  tiers,
  isLoading,
  onCreateTier,
  onUpdateTier,
  onDeleteTier,
  isCreating,
  isUpdating,
  isDeleting,
}: TierManagementEnhancedProps) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<TierDefinition | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleCreateSuccess = async (request: CreateTierDefinitionRequest) => {
    try {
      await onCreateTier(request);
      setShowCreateModal(false);
      setSuccessMessage('Tier created successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setErrorMessage('Failed to create tier');
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  const handleUpdateSuccess = async (request: UpdateTierDefinitionRequest) => {
    if (!selectedTier) return;
    try {
      await onUpdateTier({ tierId: selectedTier.tier, request });
      setShowUpdateModal(false);
      setSelectedTier(null);
      setSuccessMessage('Tier updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setErrorMessage('Failed to update tier');
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  const handleDeleteSuccess = async () => {
    if (!selectedTier) return;
    try {
      await onDeleteTier(selectedTier.tier);
      setShowDeleteModal(false);
      setSelectedTier(null);
      setSuccessMessage('Tier deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setErrorMessage('Failed to delete tier. It may have active customers.');
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  const handleEditClick = (tier: TierDefinition) => {
    setSelectedTier(tier);
    setShowUpdateModal(true);
  };

  const handleDeleteClick = (tier: TierDefinition) => {
    setSelectedTier(tier);
    setShowDeleteModal(true);
  };

  const sortedTiers = [...tiers].sort((a, b) => {
    const rankA = a.rank || a.level || 0;
    const rankB = b.rank || b.level || 0;
    return rankA - rankB;
  });

  const getTierColor = (tier: TierDefinition) => {
    if (tier.displaySettings?.color) {
      return tier.displaySettings.color;
    }
    const tierName = tier.tier.toLowerCase();
    if (tierName === 'bronze') return '#CD7F32';
    if (tierName === 'silver') return '#C0C0C0';
    if (tierName === 'gold') return '#FFD700';
    if (tierName === 'platinum') return '#E5E4E2';
    return '#6c757d';
  };

  const getBenefitsText = (benefits: string | string[]): string => {
    if (typeof benefits === 'string') return benefits;
    if (Array.isArray(benefits)) return benefits.join(', ');
    return 'N/A';
  };

  return (
    <>
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <Card.Title className="mb-1">Tier Management</Card.Title>
              <small className="text-muted">Manage loyalty program tier definitions and settings</small>
            </div>
            <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)} disabled={isCreating}>
              <Plus size={16} className="me-1" />
              Create Tier
            </Button>
          </div>

          {successMessage && (
            <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert variant="danger" dismissible onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading tier definitions...</p>
            </div>
          ) : tiers.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted mb-3">No tier definitions found</p>
              <Button variant="outline-primary" onClick={() => setShowCreateModal(true)}>
                <Plus size={16} className="me-1" />
                Create Your First Tier
              </Button>
            </div>
          ) : (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Rank/Level</th>
                  <th>Min Points</th>
                  <th>Multiplier</th>
                  <th>Benefits</th>
                  <th>Display</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTiers.map((tierDef) => (
                  <tr key={tierDef.tier}>
                    <td>
                      <div className="d-flex align-items-center">
                        {tierDef.displaySettings?.icon && (
                          <span className="me-2" style={{ fontSize: '1.5rem' }}>
                            {tierDef.displaySettings.icon}
                          </span>
                        )}
                        <Badge bg="light" text="dark" style={{ backgroundColor: getTierColor(tierDef) }}>
                          {tierDef.tier}
                        </Badge>
                      </div>
                    </td>
                    <td>{tierDef.rank || tierDef.level || 'N/A'}</td>
                    <td>{tierDef.minPoints.toLocaleString()}</td>
                    <td>{(tierDef.multiplier || tierDef.pointMultiplier || 1).toFixed(1)}x</td>
                    <td>
                      <small className="text-muted">{getBenefitsText(tierDef.benefits)}</small>
                    </td>
                    <td>
                      {tierDef.displaySettings && (
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: tierDef.displaySettings.color,
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                            }}
                          />
                          <small>Order: {tierDef.displaySettings.displayOrder}</small>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditClick(tierDef)}
                          disabled={isUpdating || isDeleting}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteClick(tierDef)}
                          disabled={isUpdating || isDeleting}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <CreateTierModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={handleCreateSuccess}
        isSubmitting={isCreating}
      />

      {selectedTier && (
        <UpdateTierDefinitionModal
          show={showUpdateModal}
          onHide={() => {
            setShowUpdateModal(false);
            setSelectedTier(null);
          }}
          onSubmit={handleUpdateSuccess}
          isSubmitting={isUpdating}
          tier={selectedTier}
        />
      )}

      {selectedTier && (
        <DeleteConfirmModal
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedTier(null);
          }}
          onConfirm={handleDeleteSuccess}
          isDeleting={isDeleting}
          tierName={selectedTier.tier}
        />
      )}
    </>
  );
};

